// @ts-nocheck
import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, BedDouble, Building2, Loader2, Plus, Pencil, Trash2, ArrowRightLeft,
} from "lucide-react";

const FN = "rle-manage-hotels";

async function callHotels(body: any) {
  const { data, error } = await invokeEdgeFunction(FN, { body });
  if (error) throw new Error(error.message || "Request failed");
  if (data && data.ok === false) throw new Error(data.error || "Request failed");
  return data;
}

const money = (n: any) =>
  n == null || n === "" ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const num = (n: any) => (n == null || n === "" ? "—" : Number(n).toLocaleString());
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : "—");

const Tile: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Card>
    <CardContent className="p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
    </CardContent>
  </Card>
);

/* ---------------- RFP form ---------------- */
const emptyRfp = {
  hotel_name: "", hotel_address: "", contact_name: "", contact_email: "", contact_phone: "",
  rooms_requested: "", start_date: "", end_date: "", desired_rate: "",
  offered_rate: "", hotel_response: "",
};

const RfpDialog = ({ open, onOpenChange, initial, onSave, busy }: any) => {
  const [form, setForm] = useState<any>(emptyRfp);
  React.useEffect(() => {
    if (open) setForm({ ...emptyRfp, ...(initial || {}) });
  }, [open, initial]);
  const set = (k: string) => (e: any) => setForm((f: any) => ({ ...f, [k]: e.target.value }));
  const isEdit = !!initial?.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{isEdit ? "Edit RFP" : "New RFP"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label>Hotel name</Label><Input value={form.hotel_name} onChange={set("hotel_name")} /></div>
          <div className="sm:col-span-2"><Label>Address</Label><Input value={form.hotel_address} onChange={set("hotel_address")} /></div>
          <div><Label>Contact name</Label><Input value={form.contact_name} onChange={set("contact_name")} /></div>
          <div><Label>Contact email</Label><Input type="email" value={form.contact_email} onChange={set("contact_email")} /></div>
          <div><Label>Contact phone</Label><Input value={form.contact_phone} onChange={set("contact_phone")} /></div>
          <div><Label>Rooms requested</Label><Input type="number" value={form.rooms_requested} onChange={set("rooms_requested")} /></div>
          <div><Label>Start date</Label><Input type="date" value={form.start_date || ""} onChange={set("start_date")} /></div>
          <div><Label>End date</Label><Input type="date" value={form.end_date || ""} onChange={set("end_date")} /></div>
          <div><Label>Desired rate</Label><Input type="number" step="0.01" value={form.desired_rate} onChange={set("desired_rate")} /></div>
          {isEdit && (
            <>
              <div><Label>Offered rate</Label><Input type="number" step="0.01" value={form.offered_rate ?? ""} onChange={set("offered_rate")} /></div>
              <div className="sm:col-span-2"><Label>Hotel response</Label><Input value={form.hotel_response ?? ""} onChange={set("hotel_response")} /></div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={busy || !form.hotel_name}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Property form ---------------- */
const emptyProperty = {
  name: "", address: "", distance_miles: "", group_rate: "", rooms_contracted: "",
  commission_pct: "", rebate_per_room: "", attrition_pct: "",
  contact_name: "", contact_email: "", contact_phone: "", booking_link: "", is_primary: false,
};

const PropertyDialog = ({ open, onOpenChange, initial, onSave, busy }: any) => {
  const [form, setForm] = useState<any>(emptyProperty);
  React.useEffect(() => {
    if (open) setForm({ ...emptyProperty, ...(initial || {}) });
  }, [open, initial]);
  const set = (k: string) => (e: any) => setForm((f: any) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit property" : "Add property"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label>Name</Label><Input value={form.name} onChange={set("name")} /></div>
          <div className="sm:col-span-2"><Label>Address</Label><Input value={form.address} onChange={set("address")} /></div>
          <div><Label>Distance (miles)</Label><Input type="number" step="0.1" value={form.distance_miles} onChange={set("distance_miles")} /></div>
          <div><Label>Group rate</Label><Input type="number" step="0.01" value={form.group_rate} onChange={set("group_rate")} /></div>
          <div><Label>Rooms contracted</Label><Input type="number" value={form.rooms_contracted} onChange={set("rooms_contracted")} /></div>
          <div><Label>Commission %</Label><Input type="number" step="0.01" value={form.commission_pct} onChange={set("commission_pct")} /></div>
          <div><Label>Rebate / room</Label><Input type="number" step="0.01" value={form.rebate_per_room} onChange={set("rebate_per_room")} /></div>
          <div><Label>Attrition %</Label><Input type="number" step="0.01" value={form.attrition_pct} onChange={set("attrition_pct")} /></div>
          <div><Label>Contact name</Label><Input value={form.contact_name} onChange={set("contact_name")} /></div>
          <div><Label>Contact email</Label><Input type="email" value={form.contact_email} onChange={set("contact_email")} /></div>
          <div><Label>Contact phone</Label><Input value={form.contact_phone} onChange={set("contact_phone")} /></div>
          <div className="sm:col-span-2"><Label>Booking link</Label><Input value={form.booking_link} onChange={set("booking_link")} /></div>
          <div className="sm:col-span-2 flex items-center gap-2 pt-1">
            <Checkbox
              id="is_primary"
              checked={!!form.is_primary}
              onCheckedChange={(v) => setForm((f: any) => ({ ...f, is_primary: !!v }))}
            />
            <Label htmlFor="is_primary">Primary hotel</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={busy || !form.name}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Booking form ---------------- */
const emptyBooking = {
  team_name: "", hotel_property_id: "", rooms: "", nightly_rate: "",
  check_in_date: "", check_out_date: "", confirmation_number: "",
};

const BookingDialog = ({ open, onOpenChange, initial, properties, onSave, busy }: any) => {
  const [form, setForm] = useState<any>(emptyBooking);
  React.useEffect(() => {
    if (open) setForm({ ...emptyBooking, ...(initial || {}) });
  }, [open, initial]);
  const set = (k: string) => (e: any) => setForm((f: any) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit booking" : "Add booking"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label>Team name</Label><Input value={form.team_name} onChange={set("team_name")} /></div>
          <div className="sm:col-span-2">
            <Label>Property</Label>
            <Select
              value={form.hotel_property_id || undefined}
              onValueChange={(v) => setForm((f: any) => ({ ...f, hotel_property_id: v }))}
            >
              <SelectTrigger><SelectValue placeholder="Select a property" /></SelectTrigger>
              <SelectContent>
                {(properties || []).map((p: any) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Rooms</Label><Input type="number" value={form.rooms} onChange={set("rooms")} /></div>
          <div><Label>Nightly rate</Label><Input type="number" step="0.01" value={form.nightly_rate} onChange={set("nightly_rate")} /></div>
          <div><Label>Check-in</Label><Input type="date" value={form.check_in_date || ""} onChange={set("check_in_date")} /></div>
          <div><Label>Check-out</Label><Input type="date" value={form.check_out_date || ""} onChange={set("check_out_date")} /></div>
          <div className="sm:col-span-2"><Label>Confirmation #</Label><Input value={form.confirmation_number} onChange={set("confirmation_number")} /></div>
        </div>
        <p className="text-xs text-muted-foreground">
          Room nights, revenue, and commission are calculated automatically after saving.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={busy || !form.team_name || !form.hotel_property_id}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Page ---------------- */
export default function OperatorEventHotelsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const [rfpDialog, setRfpDialog] = useState<{ open: boolean; initial: any }>({ open: false, initial: null });
  const [propDialog, setPropDialog] = useState<{ open: boolean; initial: any }>({ open: false, initial: null });
  const [bookingDialog, setBookingDialog] = useState<{ open: boolean; initial: any }>({ open: false, initial: null });

  const summaryQuery = useQuery({
    queryKey: ["hotels-summary", eventId],
    enabled: !!eventId,
    queryFn: async () => (await callHotels({ action: "summary", event_id: eventId }))?.summary ?? {},
  });

  const listQuery = useQuery({
    queryKey: ["hotels-list", eventId],
    enabled: !!eventId,
    queryFn: async () => await callHotels({ action: "list", event_id: eventId }),
  });

  const rfps = listQuery.data?.rfps ?? [];
  const properties = listQuery.data?.properties ?? [];
  const bookings = listQuery.data?.bookings ?? [];
  const summary = summaryQuery.data ?? {};

  const propertyName = useMemo(() => {
    const m: Record<string, string> = {};
    properties.forEach((p: any) => { m[p.id] = p.name; });
    return m;
  }, [properties]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["hotels-list", eventId] });
    qc.invalidateQueries({ queryKey: ["hotels-summary", eventId] });
  };

  const run = async (body: any, successMsg: string, close?: () => void) => {
    setBusy(true);
    try {
      await callHotels(body);
      toast.success(successMsg);
      close?.();
      refresh();
    } catch (e: any) {
      toast.error(e.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = (msg: string) => window.confirm(msg);

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div>
        <button
          onClick={() => navigate(-1)}
          className="mb-2 flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </button>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <BedDouble className="h-6 w-6" /> Hotels
        </h1>
        <p className="text-muted-foreground">Manage RFPs, contracted properties, and team bookings for this event.</p>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="rfps">RFPs</TabsTrigger>
          <TabsTrigger value="properties">Properties</TabsTrigger>
          <TabsTrigger value="bookings">Bookings</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="mt-4">
          {summaryQuery.isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Tile label="Properties" value={num(summary.properties ?? summary.property_count)} />
              <Tile label="Rooms contracted" value={num(summary.rooms_contracted)} />
              <Tile label="Bookings" value={num(summary.bookings ?? summary.booking_count)} />
              <Tile label="Room nights" value={num(summary.room_nights)} />
              <Tile label="Gross revenue" value={money(summary.gross_revenue)} />
              <Tile label="Commission earned" value={money(summary.commission_earned)} />
              <Tile label="Rebate earned" value={money(summary.rebate_earned)} />
            </div>
          )}
        </TabsContent>

        {/* RFPs */}
        <TabsContent value="rfps" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setRfpDialog({ open: true, initial: null })}>
              <Plus className="mr-2 h-4 w-4" /> New RFP
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Hotel</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Rooms</TableHead>
                    <TableHead>Desired</TableHead>
                    <TableHead>Offered</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rfps.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">No RFPs yet</TableCell></TableRow>
                  )}
                  {rfps.map((r: any) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">
                        {r.hotel_name}
                        <div className="text-xs text-muted-foreground">{r.hotel_address}</div>
                      </TableCell>
                      <TableCell>{fmtDate(r.start_date)} – {fmtDate(r.end_date)}</TableCell>
                      <TableCell>{num(r.rooms_requested)}</TableCell>
                      <TableCell>{money(r.desired_rate)}</TableCell>
                      <TableCell>{money(r.offered_rate)}</TableCell>
                      <TableCell><Badge variant="secondary">{r.status || "draft"}</Badge></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setRfpDialog({ open: true, initial: r })}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {r.status !== "converted" && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={busy}
                              onClick={() => run({ action: "rfp_convert", id: r.id }, "RFP converted to property")}
                            >
                              <ArrowRightLeft className="mr-2 h-4 w-4" /> Convert
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Properties */}
        <TabsContent value="properties" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setPropDialog({ open: true, initial: null })}>
              <Plus className="mr-2 h-4 w-4" /> Add property
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Distance</TableHead>
                    <TableHead>Group rate</TableHead>
                    <TableHead>Rooms</TableHead>
                    <TableHead>Commission</TableHead>
                    <TableHead>Rebate</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {properties.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">No properties yet</TableCell></TableRow>
                  )}
                  {properties.map((p: any) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          {p.name}
                          {p.is_primary && <Badge>Primary</Badge>}
                        </span>
                        <div className="text-xs text-muted-foreground">{p.address}</div>
                      </TableCell>
                      <TableCell>{p.distance_miles != null ? `${p.distance_miles} mi` : "—"}</TableCell>
                      <TableCell>{money(p.group_rate)}</TableCell>
                      <TableCell>{num(p.rooms_contracted)}</TableCell>
                      <TableCell>{p.commission_pct != null ? `${p.commission_pct}%` : "—"}</TableCell>
                      <TableCell>{money(p.rebate_per_room)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setPropDialog({ open: true, initial: p })}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busy}
                            onClick={() => confirmDelete(`Delete ${p.name}?`) && run({ action: "property_delete", id: p.id }, "Property deleted")}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bookings */}
        <TabsContent value="bookings" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setBookingDialog({ open: true, initial: null })} disabled={properties.length === 0}>
              <Plus className="mr-2 h-4 w-4" /> Add booking
            </Button>
          </div>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Team bookings</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Team</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Stay</TableHead>
                    <TableHead>Rooms</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Room nights</TableHead>
                    <TableHead>Revenue</TableHead>
                    <TableHead>Commission</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.length === 0 && (
                    <TableRow><TableCell colSpan={9} className="py-8 text-center text-muted-foreground">No bookings yet</TableCell></TableRow>
                  )}
                  {bookings.map((b: any) => (
                    <TableRow key={b.id}>
                      <TableCell className="font-medium">
                        {b.team_name}
                        {b.confirmation_number && (
                          <div className="text-xs text-muted-foreground">#{b.confirmation_number}</div>
                        )}
                      </TableCell>
                      <TableCell>{b.property_name || propertyName[b.hotel_property_id] || "—"}</TableCell>
                      <TableCell>{fmtDate(b.check_in_date)} – {fmtDate(b.check_out_date)}</TableCell>
                      <TableCell>{num(b.rooms)}</TableCell>
                      <TableCell>{money(b.nightly_rate)}</TableCell>
                      <TableCell>{num(b.room_nights)}</TableCell>
                      <TableCell>{money(b.gross_revenue ?? b.total_revenue)}</TableCell>
                      <TableCell>{money(b.commission_amount ?? b.commission)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setBookingDialog({ open: true, initial: b })}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busy}
                            onClick={() => confirmDelete(`Delete booking for ${b.team_name}?`) && run({ action: "booking_delete", id: b.id }, "Booking deleted")}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <RfpDialog
        open={rfpDialog.open}
        initial={rfpDialog.initial}
        busy={busy}
        onOpenChange={(o: boolean) => setRfpDialog((s) => ({ ...s, open: o }))}
        onSave={(form: any) =>
          run(
            { action: "rfp_save", event_id: eventId, id: rfpDialog.initial?.id, rfp: form },
            "RFP saved",
            () => setRfpDialog({ open: false, initial: null }),
          )
        }
      />

      <PropertyDialog
        open={propDialog.open}
        initial={propDialog.initial}
        busy={busy}
        onOpenChange={(o: boolean) => setPropDialog((s) => ({ ...s, open: o }))}
        onSave={(form: any) =>
          run(
            { action: "property_save", event_id: eventId, id: propDialog.initial?.id, property: form },
            "Property saved",
            () => setPropDialog({ open: false, initial: null }),
          )
        }
      />

      <BookingDialog
        open={bookingDialog.open}
        initial={bookingDialog.initial}
        properties={properties}
        busy={busy}
        onOpenChange={(o: boolean) => setBookingDialog((s) => ({ ...s, open: o }))}
        onSave={(form: any) =>
          run(
            { action: "booking_save", event_id: eventId, id: bookingDialog.initial?.id, booking: form },
            "Booking saved",
            () => setBookingDialog({ open: false, initial: null }),
          )
        }
      />
    </div>
  );
}
